/**
 * @module UseVoipPartySelection
 * @description Phase 16 extraction of caller/callee selection mutations for the VoIP detail form.
 * RHF note: updates party fields and agents.ids together so shouldUnregister:true submits stay complete.
 */

import { SHOULD_DIRTY_TRUE } from '../../../ServiceTestsFormControlConstants';
import { applySelectionToForm, buildPartyConfig, clearPartyFromForm } from './VoipCallParticipantsHelpers';
import {
    AGENT_ALL_FIELD_NAME,
    AGENT_IDS_FIELD_NAME,
    TESTCONFIG_CALLVIA_FIELD_NAME,
} from '../../../ServiceTestsConstants';

function applySingleAgentSelection({ role, agent, id, setValue, resetCallVia }) {
    if (resetCallVia) {
        setValue(TESTCONFIG_CALLVIA_FIELD_NAME, {}, SHOULD_DIRTY_TRUE);
    }

    setValue(`testConfig.${role}`, buildPartyConfig(agent), SHOULD_DIRTY_TRUE);
    setValue(AGENT_IDS_FIELD_NAME, [id], SHOULD_DIRTY_TRUE);
    setValue(AGENT_ALL_FIELD_NAME, false, SHOULD_DIRTY_TRUE);
}

function applyBothPartySelection({ role, selectedAgent, otherAgent, setValue, existingCallVia }) {
    if (role === 'caller') {
        applySelectionToForm(selectedAgent, otherAgent, setValue, existingCallVia);
        return;
    }

    applySelectionToForm(otherAgent, selectedAgent, setValue, existingCallVia);
}

function createPartySelectHandler({
    role,
    otherRole,
    setSelectedId,
    getSelectedOtherId,
    setOtherSelectedId,
    agentsById,
    setValue,
    getValues,
    shouldClearOtherSelection,
}) {
    return (event) => {
        const id = event.target.value;
        const selectedAgent = agentsById[id];

        if (!selectedAgent) {
            return;
        }

        setSelectedId(id);

        const selectedOtherId = getSelectedOtherId();
        const otherAgent = selectedOtherId ? agentsById[selectedOtherId] : null;

        if (shouldClearOtherSelection({ id, selectedAgent, selectedOtherId, otherAgent })) {
            setOtherSelectedId('');
            clearPartyFromForm(otherRole, setValue);
            applySingleAgentSelection({
                role,
                agent: selectedAgent,
                id,
                setValue,
                resetCallVia: true,
            });
            return;
        }

        setValue(`testConfig.${role}`, buildPartyConfig(selectedAgent), SHOULD_DIRTY_TRUE);

        if (otherAgent) {
            const existingCallVia = getValues(TESTCONFIG_CALLVIA_FIELD_NAME);
            applyBothPartySelection({
                role,
                selectedAgent,
                otherAgent,
                setValue,
                existingCallVia,
            });
            return;
        }

        applySingleAgentSelection({
            role,
            agent: selectedAgent,
            id,
            setValue,
            resetCallVia: false,
        });
    };
}

export function useVoipPartySelection({
    agentsById,
    selectedCallerId,
    selectedCalleeId,
    setSelectedCallerId,
    setSelectedCalleeId,
    setValue,
    getValues,
}) {
    const handleCallerSelect = createPartySelectHandler({
        role: 'caller',
        otherRole: 'callee',
        setSelectedId: setSelectedCallerId,
        getSelectedOtherId: () => selectedCalleeId,
        setOtherSelectedId: setSelectedCalleeId,
        agentsById,
        setValue,
        getValues,
        shouldClearOtherSelection: ({ id, selectedAgent, selectedOtherId, otherAgent }) => {
            if (id === selectedOtherId) {
                return true;
            }

            return !!(otherAgent && otherAgent.useDirectCalling !== selectedAgent.useDirectCalling);
        },
    });

    const handleCalleeSelect = createPartySelectHandler({
        role: 'callee',
        otherRole: 'caller',
        setSelectedId: setSelectedCalleeId,
        getSelectedOtherId: () => selectedCallerId,
        setOtherSelectedId: setSelectedCallerId,
        agentsById,
        setValue,
        getValues,
        shouldClearOtherSelection: ({ id, selectedOtherId }) => id === selectedOtherId,
    });

    return {
        handleCallerSelect,
        handleCalleeSelect,
    };
}
